import React from 'react';
import { Card, Spinner, Alert } from 'react-bootstrap';

/**
 * Generation Progress Component
 * Shows step-by-step progress during article processing with real-time updates
 */
const GenerationProgress = ({ progress = {}, creditInfo = null }) => {

  const steps = [
    { id: 'extract', name: 'Extracting article content' },
    { id: 'audio', name: 'Generating audio narration' },
    { id: 'social', name: 'Creating social posts' },
    { id: 'images', name: 'Generating images' },
    { id: 'finalizing', name: 'Finalizing results' }
  ];

  const getStepStatus = (stepId) => {
    const stepProgress = progress[stepId];
    if (!stepProgress) return 'pending';
    return stepProgress.status;
  };

  const getStepDetail = (stepId) => {
    const stepProgress = progress[stepId];
    if (stepId === 'images' && stepProgress?.total) {
      return `(${stepProgress.completed || 0}/${stepProgress.total})`;
    }
    return null;
  };

  const renderStepIcon = (status, index) => {
    if (status === 'completed') {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      );
    }
    if (status === 'in_progress') {
      return <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} />;
    }
    return index + 1;
  };

  const getStepStyle = (status) => {
    if (status === 'completed') {
      return { backgroundColor: '#198754', color: '#fff' }; // green
    }
    if (status === 'in_progress') {
      return { backgroundColor: '#0d6efd', color: '#fff' }; // blue
    }
    return { backgroundColor: '#e9ecef', color: '#6c757d' }; // gray
  };

  return (
    <Card style={{ maxWidth: '500px', margin: '0 auto' }}>
      <Card.Body className="text-center">
        <Spinner animation="border" variant="primary" className="mb-4" />
        <Card.Title className="mb-4">Generating Your Media</Card.Title>

        {/* Credit info message */}
        {creditInfo?.usedFallback && (
          <Alert variant="info" className="text-start mb-4" style={{ fontSize: '0.875rem' }}>
            <i className="bi bi-info-circle me-2"></i>
            {creditInfo.message}
          </Alert>
        )}

        <div style={{ textAlign: 'left' }}>
          {steps.map((step, index) => {
            const status = getStepStatus(step.id);
            const detail = getStepDetail(step.id);
            return (
              <div
                key={step.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 0',
                  borderBottom: index < steps.length - 1 ? '1px solid #dee2e6' : 'none'
                }}
              >
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px',
                    fontSize: '12px',
                    ...getStepStyle(status)
                  }}
                >
                  {renderStepIcon(status, index)}
                </div>
                <span style={{
                  color: status === 'pending' ? '#6c757d' : '#212529',
                  fontWeight: status === 'in_progress' ? '500' : 'normal'
                }}>
                  {step.name}
                  {detail && <span className="text-muted ms-1" style={{ fontSize: '0.875rem' }}>{detail}</span>}
                </span>
              </div>
            );
          })}
        </div>

        <p className="text-muted mt-4" style={{ fontSize: '0.875rem' }}>
          This may take 30-60 seconds. Please don't close this page.
        </p>
      </Card.Body>
    </Card>
  );
};

export default GenerationProgress;
